import { forwardRef, useImperativeHandle, ChangeEvent } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import Modal from '@sdk/components/ui/modal/Modal';
import ModalErrorDisplay from '@sdk/components/ui/modal/components/modalErrorDisplay/ModalErrorDisplay';
import Row from '@sdk/components/ui/spacing/Row';
import H3 from '@sdk/components/ui/typography/H3';
import Text14 from '@sdk/components/ui/typography/Text14';
import Button from '@sdk/components/ui/buttons/Button';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import TextField from '@sdk/components/forms/TextField';
import Switcher from '@sdk/components/forms/Switcher';
import gettext from '@sdk/lang/gettext';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { IdField } from '@sdk/types';

import EmailField from '@web/onboarding/components/EmailField';
import {
  ADD_EMPLOYEES_SAVE_BTN,
  ADD_EMPLOYEES_TEXT,
  FIRST_AND_LAST,
  FULL_NAME_INPUT_FIELD,
} from '@web/onboarding/Checklist/AddEmployeesModal/constants';

import type { AddPersonaCollectionUser } from '../onboardingChecklist.types';
import {
  Container,
  CenterContentRow,
  FooterRow,
  ButtonContainer,
  ModalContent,
  ScrollableArea,
} from '../components/modalBase.style';

import useAddEmployeesModal from './addEmployeesModal.hooks';

export const SCROLLABLE_AREA_MAX_HEIGHT = 385;

type ForwardedAttrs = {
  open: () => void;
};

type Props = {
  currentUser: AuthUserType;
  locations: IdField[];
  addPersonaCollectionUser: AddPersonaCollectionUser;
  onCloseCallback?: () => void;
};

const AddEmployeeModal = forwardRef<ForwardedAttrs, Props>(
  ({ currentUser, locations, addPersonaCollectionUser, onCloseCallback }, ref) => {
    const { isVisible, open, close: closeModal } = useModalState();

    const close = () => {
      closeModal();
      onCloseCallback();
    };

    const {
      focusFieldRef,
      config,
      fields,
      errors,
      onShouldSendInviteChange,
      onFullNameChange,
      submitHandler,
      closeHandler,
    } = useAddEmployeesModal({
      locations,
      currentUser,
      addPersonaCollectionUser,
      close,
    });

    const formHasErrors = fields.employees.some(
      ({ email, fullName }) => email.error || fullName.error
    );

    useImperativeHandle(ref, () => ({
      open,
    }));

    if (!isVisible) {
      return null;
    }

    return (
      <Modal fixed config={config} isVisible={isVisible} onDismiss={closeHandler}>
        <Container>
          <CenterContentRow pTop={20} pBottom={20}>
            <H3>{gettext(ADD_EMPLOYEES_TEXT)}</H3>
          </CenterContentRow>

          <ModalContent>
            <ScrollableArea pLeft={15} pRight={15} maxHeight={SCROLLABLE_AREA_MAX_HEIGHT}>
              {fields.employees.map((field, index) => (
                <Row key={index} mTop={15}>
                  <TextField
                    data-testid={FULL_NAME_INPUT_FIELD}
                    ref={index === 0 ? focusFieldRef : null}
                    name="fullname"
                    value={field.fullName.value}
                    placeholder={gettext(FIRST_AND_LAST)}
                    label={index === 0 ? gettext(FIRST_AND_LAST) : null}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      onFullNameChange(e.target.value, field, index);
                    }}
                    height={INPUT_HEIGHT}
                    invalid={Boolean(field.fullName.error)}
                    error={field.fullName.error}
                  />
                  <ModalContent left={10}>
                    <EmailField
                      skipLabel={index !== 0}
                      value={field.email.value}
                      onChange={field.email.onChange}
                      onBlur={field.email.onBlur}
                      invalid={Boolean(field.email.error)}
                      error={field.email.error}
                    />
                  </ModalContent>
                </Row>
              ))}
            </ScrollableArea>
            <Row
              mTop={30}
              pLeft={15}
              pRight={15}
              justifyContent="space-between"
              alignItems="center">
              <Text14 color={ThemeColorType.gray} semiBold>
                {gettext('Invite employees to join Sling')}
              </Text14>
              <Switcher
                small
                value={fields.shouldSendInvite.value}
                onChange={onShouldSendInviteChange}
              />
            </Row>
          </ModalContent>

          <FooterRow mTop={20} pLeft={15} pRight={15}>
            <ButtonContainer pRight={10}>
              <Button buttonType={ButtonType.default} onClick={closeHandler}>
                {gettext('Cancel')}
              </Button>
            </ButtonContainer>

            <ButtonContainer>
              <ProgressButton
                data-testid={ADD_EMPLOYEES_SAVE_BTN}
                buttonType={ButtonType.primary}
                disabled={formHasErrors}
                onClick={submitHandler}
                height={INPUT_HEIGHT}>
                {gettext('Save')}
              </ProgressButton>
            </ButtonContainer>
          </FooterRow>
        </Container>
        <ModalErrorDisplay error={errors?.length ? errors[0].message : ''} />
      </Modal>
    );
  }
);

AddEmployeeModal.displayName = 'AddEmployeeModal';

export default AddEmployeeModal;
